<template>
  <v-card class="step-tab-panel" flat>
    <v-container class="pa-0 ma-0 switching-container d-flex flex-column">
      <ScenarioMeasuringHeader
        :header-text="tkey('infoNote')"
        :tooltip-link="tooltipLink"
        :show-job="jobType"
        :disabled="isEditingDisabled"
        @trigger-job="triggerJob"
      />
      <v-row v-if="isJobComplete" no-gutters class="cann-group-container flex-grow-1">
        <v-col :cols="2">
          <CannGroupMenu
            v-if="isJobComplete"
            :selected-cann-group="selectedCannGroup ? selectedCannGroup.key : null"
            @on-cann-group-selected="selectCannGroup"
          />
        </v-col>

        <v-col :cols="10" class="h-100">
          <v-container v-if="isJobComplete" class="ma-0 pa-0 controls color rounded">
            <v-row no-gutters class="d-flex align-center px-2 py-1">
              <v-col cols="auto" class="pr-4">
                <BaseToggle
                  v-model="viewBySelection"
                  :left-toggle="toggle.leftToggle"
                  :right-toggle="toggle.rightToggle"
                />
              </v-col>
              <v-col
                v-if="viewBySelection !== 'product'"
                cols="auto"
                class="pr-2 d-flex align-center"
              >
                <span class="font-weight-bold">{{ tkey('attributeLevelSwitching') }}:</span>
              </v-col>

              <v-col v-if="viewBySelection !== 'product'" cols="auto">
                <v-select
                  v-model="selectedAttribute"
                  :items="scenarioAttributesList"
                  width="180px"
                  :placeholder="tkey('selectAttribute')"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>

          <v-divider />

          <div v-if="isJobComplete">
            <switching-matrix
              v-if="selectedCannGroup"
              :key="matrixKey"
              :cann-group="selectedCannGroup"
              :matrix-options="matrixOptions"
            />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="!isJobComplete && requiresAnalyticsText">
        <div v-if="!isAnyJobRunning" class="no-matrices">
          {{ requiresAnalyticsText }}
        </div>

        <template v-if="isAnyJobRunning">
          <v-col cols="12" class="d-flex flex-column align-center">
            <ProgressBar
              :percentage="jobProgress"
              :message="$t('messages.inProgress')"
              :status="jobStatus"
            />
            <div v-if="serviceJobCannGroups.length" class="switching-cann-group-status-list mt-4">
              <div class="text-body-2 text-medium-emphasis text-center mb-2">
                {{
                  tkey('cannGroupStatusSummary', {
                    total: serviceJobCannGroupsSummary.total,
                    running: serviceJobCannGroupsSummary.inProgress,
                    completed: serviceJobCannGroupsSummary.completed,
                  })
                }}
              </div>
              <div class="cann-group-chip-list">
                <v-chip
                  v-for="group in sortedServiceJobCannGroups"
                  :key="group.cannGroupId"
                  :title="group.name || group.cannGroupId"
                  size="small"
                  variant="outlined"
                  class="cann-group-chip"
                >
                  <v-progress-circular
                    v-if="group.status === switchingJobStatuses.inProgress"
                    size="14"
                    width="2"
                    color="primary"
                    indeterminate
                    class="mr-1"
                  />
                  <v-icon
                    v-else
                    :color="getCannGroupStatusIconColor(group.status)"
                    size="small"
                    start
                  >
                    {{ getCannGroupStatusIcon(group.status) }}
                  </v-icon>
                  <span class="cann-group-chip__text">{{ group.name || group.cannGroupId }}</span>
                </v-chip>
              </div>
            </div>
          </v-col>
        </template>
      </v-row>
      <div class="page-actions-container">
        <PageActions
          show-export
          :export-options="exportOptions"
          :save-disabled="isEditingDisabled"
          :is-discard-enabled="!isEditingDisabled"
          :is-export-enabled="isExportButtonEnabled"
          :is-custom-export="viewBySelection === 'attribute'"
          :show-save-button="false"
          :show-discard="false"
          :has-data-errors="true"
          :has-data-changes="false"
          @product-level-export="onExport(switchingReport.products)"
          @top-partners-export="onExport(switchingReport.topPartners)"
          @export="onExport(switchingReport.attributes)"
        />
      </div>
    </v-container>
  </v-card>
</template>

<script>
import { map, sortBy } from 'lodash';
import { mapState, mapActions } from 'pinia';
import jobApiEnums from '@enums/jobapi';
import switchingReport from '@enums/switching-report';
import switchingJobStatuses from '@enums/switching-job-statuses';
import downloadFile from '@/mixins/download-file';
import { useScenariosStore } from '@/stores/scenarios';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';
import { useInternationalization } from '@/composables/use-internationalization';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useSwitchingMatricesStore } from '@/stores/switching-matrices';
import { useWorkpackagesStore } from '@/stores/workpackages';

export default {
  mixins: [downloadFile],
  props: {
    requiresAnalyticsText: {
      type: String,
      required: false,
      default: '',
    },
    tooltipLink: {
      type: String,
      required: false,
      default: null,
    },
    jobType: {
      type: String,
      required: false,
      default: jobApiEnums.jobTypes.switching,
    },
    serviceJobCannGroups: {
      type: Array,
      required: false,
      default: () => [],
    },
    serviceJobProgress: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  setup() {
    const { canEditSwitching, canTriggerPrepareSwaps } = usePermission();
    const { tkey } = useInternationalization('switchingBehaviour');
    const { productSwitchingServiceEnabled } = useFeature();

    return {
      canEditSwitching,
      canTriggerPrepareSwaps,
      tkey,
      productSwitchingServiceEnabled,
    };
  },

  data() {
    return {
      switchingReport,
      selectedAttribute: null,
      viewBySelection: 'attribute',
      selectedCannGroup: null,
      scenarioAttributes: [],
      scenarioAttributesList: [],
      jobApiEnums,
      switchingJobStatuses,
      toggle: {
        leftToggle: {
          value: 'attribute',
          translation: this.tkey('attributeBtn'),
        },
        rightToggle: {
          value: 'product',
          translation: this.tkey('productBtn'),
        },
      },
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario', 'jobStatuses', 'isJobRunning']),
    ...mapState(useScenarioProductsStore, ['scenarioProducts']),
    ...mapState(useSwitchingMatricesStore, ['switchingStatus']),
    ...mapState(useWorkpackagesStore, ['isSimpleSwapsWP']),

    matrixOptions() {
      let attribute = this.selectedAttribute;
      if (this.viewBySelection === 'product') {
        attribute = null;
      }
      return {
        selectedAttribute: attribute,
        displayOption: this.viewBySelection,
      };
    },

    matrixKey() {
      const options = this.matrixOptions;
      return `${this.selectedCannGroup.key}-${options.displayOption}-${options.selectedAttribute}`;
    },

    jobProgress() {
      if (this.isPrepareSwapsJobRunning) {
        if (this.productSwitchingServiceEnabled) {
          return this.serviceJobProgress ?? 0;
        }
        return this.jobStatuses.prepareSwaps.progress;
      }
      if (this.productSwitchingServiceEnabled) {
        return this.serviceJobProgress ?? 0;
      }
      return this.jobStatuses.switching.progress;
    },

    jobStatus() {
      if (this.isPrepareSwapsJobRunning) {
        return this.jobStatuses.prepareSwaps.status;
      }
      return this.jobStatuses.switching.status;
    },

    isSwitchingJobRunning() {
      return this.isJobRunning('switching');
    },

    isPrepareSwapsJobRunning() {
      return this.isJobRunning('prepareSwaps');
    },

    isAnyJobRunning() {
      return this.isSwitchingJobRunning || this.isPrepareSwapsJobRunning;
    },

    isSwitchingComplete() {
      return (
        !this.isSwitchingJobRunning &&
        this.jobStatuses.switching.status === jobApiEnums.jobStatuses.finished
      );
    },

    isPrepareSwapsComplete() {
      return (
        !this.isPrepareSwapsJobRunning &&
        this.jobStatuses.prepareSwaps.status === jobApiEnums.jobStatuses.finished
      );
    },

    isJobComplete() {
      if (this.jobType === jobApiEnums.jobTypes.prepareSwaps) {
        return this.isPrepareSwapsComplete;
      }
      return this.isSwitchingComplete;
    },

    exportOptions() {
      if (this.viewBySelection === 'product') {
        return [
          { name: this.tkey('productLevel'), value: 'productLevelExport' },
          { name: this.tkey('topPartners'), value: 'topPartnersExport' },
        ];
      }

      return [];
    },

    isExportButtonEnabled() {
      if (this.isAnyJobRunning) return false;

      if (this.viewBySelection === 'attribute') {
        return !!this.selectedAttribute;
      }

      return true;
    },

    isEditingDisabled() {
      if (this.isSimpleSwapsWP) return !this.canTriggerPrepareSwaps;
      return !this.canEditSwitching;
    },

    sortedServiceJobCannGroups() {
      // Fixed alphabetical order regardless of status, so chip positions stay stable
      // across poll ticks instead of reshuffling as groups transition status.
      return [...this.serviceJobCannGroups].sort((a, b) =>
        (a.name || a.cannGroupId).localeCompare(b.name || b.cannGroupId)
      );
    },
    serviceJobCannGroupsSummary() {
      const cannGroups = this.serviceJobCannGroups;
      return {
        total: cannGroups.length,
        inProgress: cannGroups.filter(g => g.status === switchingJobStatuses.inProgress).length,
        completed: cannGroups.filter(g =>
          switchingJobStatuses.terminalSwitchingJobStatuses.has(g.status)
        ).length,
      };
    },
  },

  watch: {
    viewBySelection() {
      this.$emit('context-changed');
    },
    selectedAttribute() {
      this.$emit('context-changed');
    },
  },

  async created() {
    const scenarioProducts = await this.fetchScenarioProducts(this.selectedScenario);
    // check if it's needed here - previously was side effect of fetchScenarioProducts
    this.setScenarioProducts(scenarioProducts);

    this.scenarioAttributes = map(this.scenarioProducts, sp => ({
      customAttributes: sp.customAttributes,
      productKey: sp.productKey,
    }));
    this.scenarioAttributesList = sortBy(
      map(this.selectedScenario.customAttributes, 'name'),
      item => item.toLowerCase()
    );

    // Emit context change after initial setup
    this.$nextTick(() => {
      this.$emit('context-changed');
    });
  },

  methods: {
    ...mapActions(useScenarioProductsStore, ['fetchScenarioProducts', 'setScenarioProducts']),
    ...mapActions(useSwitchingMatricesStore, ['exportSwitching']),
    ...mapActions(useScenariosStore, ['loadScenario', 'updateScenario']),

    getCannGroupStatusIcon(status) {
      if (status === switchingJobStatuses.completed) return 'mdi-check-circle';
      if (status === switchingJobStatuses.failed) return 'mdi-close-circle';
      if (status === switchingJobStatuses.cancelled) return 'mdi-cancel';
      if (status === switchingJobStatuses.timedOut) return 'mdi-timer-alert-outline';
      if (status === switchingJobStatuses.inProgress) return 'mdi-loading';
      return 'mdi-clock-outline'; // pending or unrecognised
    },

    getCannGroupStatusIconColor(status) {
      if (status === switchingJobStatuses.completed) return 'success';
      if (status === switchingJobStatuses.failed) return 'error';
      if (status === switchingJobStatuses.cancelled) return 'grey';
      if (status === switchingJobStatuses.timedOut) return 'warning';
      if (status === switchingJobStatuses.inProgress) return 'primary';
      return 'grey';
    },

    selectCannGroup(cg) {
      this.selectedCannGroup = cg;
      this.$emit('context-changed');
    },

    async onExport(level) {
      const options = {
        type: level,
        selectedAttribute: this.selectedAttribute,
        translations: {
          metadata: this.tkey('excel.metadata'),
          workpackageName: this.tkey('excel.workpackageName'),
          scenarioName: this.tkey('excel.scenarioName'),
          extractTime: this.tkey('excel.extractTime'),
          user: this.tkey('excel.user'),
          products: this.tkey('excel.products'),
          cannGroup: this.tkey('excel.cannGroup'),
          cannGroups: this.tkey('excel.cannGroups'),
          productIdentifier: this.tkey('excel.productIdentifier'),
          tab: this.tkey('excel.tab'),
          overview: this.tkey('excel.overview'),
          description: this.tkey('excel.description'),
          attribute: this.tkey('excel.attribute'),
          rank: this.tkey('excel.rank'),
          probability: this.tkey('excel.probability'),
          newProductsNotIncludedNote: this.tkey('excel.newProductsNotIncludedNote'),
        },
      };

      const { buffer, filename } = await this.exportSwitching(options);
      this.downloadFromBuffer(buffer, filename);
    },

    triggerJob(jobType) {
      const { prepareSwaps, switching } = jobApiEnums.jobTypes;

      const events = {
        [switching]: 'run-switching',
        [prepareSwaps]: 'run-prepare-swaps',
      };

      this.$emit(events[jobType]);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.switching-container {
  max-width: none;
  height: 100%;
  .header {
    border-bottom: 1px solid $assortment-divider-colour;
  }

  .controls {
    max-width: none;
  }

  .cann-group-container {
    overflow: auto;
    height: $tab-panel-height;
  }

  .attribute-level-title {
    p {
      font-size: 1.2rem;
      font-weight: bold;
      margin-bottom: 0 !important;
      margin-right: 7px;
    }
  }

  .v-item-group {
    margin-right: 5px;
  }

  .v-input {
    margin-right: 5px;
  }

  .no-matrices {
    margin-top: 60px;
    font-size: 1.5rem;
    text-align: center;
    width: 100%;
  }

  .switching-cann-group-status-list {
    width: 100%;
    max-width: 600px;

    .cann-group-chip-list {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      gap: 6px;
      max-height: 240px;
      overflow-y: auto;
    }

    .cann-group-chip {
      font-size: 0.8rem;

      &__text {
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
